/**
 * Enhanced MediaPost Component with YouTube Support
 * 
 * Allows posting to multiple platforms including YouTube.
 * Handles video uploads with progress tracking and validation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import MediaTagOverlay from './MediaTagOverlay';
import {
  uploadToYouTube,
  checkYouTubeConnection,
  validateYouTubeFilePath,
  extractYouTubeTitleDescription
} from '../../services/youtubeService';

const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

// Platform configurations
const PLATFORMS = {
  instagram: { name: 'Instagram', icon: '📷', supportsVideo: true, supportsImage: true },
  facebook: { name: 'Facebook', icon: '📘', supportsVideo: true, supportsImage: true },
  twitter: { name: 'Twitter/X', icon: '🐦', supportsVideo: true, supportsImage: true },
  linkedin: { name: 'LinkedIn', icon: '💼', supportsVideo: true, supportsImage: true },
  youtube: { name: 'YouTube', icon: '▶️', supportsVideo: true, supportsImage: false },
  tiktok: { name: 'TikTok', icon: '🎵', supportsVideo: true, supportsImage: false },
  bluesky: { name: 'Bluesky', icon: '🦋', supportsVideo: true, supportsImage: true },
  wechat: { name: 'WeChat', icon: '💬', supportsVideo: true, supportsImage: true },

  snapchat: { name: 'Snapchat', icon: '👻', supportsVideo: true, supportsImage: true },
  reddit: { name: 'Reddit', icon: '🤖', supportsVideo: true, supportsImage: true },
  pinterest: { name: 'Pinterest', icon: '📌', supportsVideo: false, supportsImage: true },
  quora: { name: 'Quora', icon: '❓', supportsVideo: false, supportsImage: true },
  line: { name: 'Line', icon: '💚', supportsVideo: true, supportsImage: true },
  twitch: { name: 'Twitch', icon: '🎮', supportsVideo: true, supportsImage: true },
  discord: { name: 'Discord', icon: '🎧', supportsVideo: true, supportsImage: true }
};

function MediaPostEnhanced() {
  const { tenant } = useParams();
  const location = useLocation();
  
  // Get event data from location state if available
  const eventData = location.state?.eventData || {
    id: null,
    title: '',
    description: '',
    images: [],
    videos: []
  };

  // State management
  const [postContent, setPostContent] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [platformStatus, setPlatformStatus] = useState({});
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  
  // User tags on media (per image index): { [imageIdx]: [{ username, x, y }] }
  const [userTags, setUserTags] = useState({});

  // YouTube-specific state
  const [youtubePrivacy, setYoutubePrivacy] = useState('unlisted');
  const [youtubeTags, setYoutubeTags] = useState('');
  const [youtubeIsShort, setYoutubeIsShort] = useState(false);
  
  // Get tenant ID
  const getTenantId = useCallback(() => {
    return Number(tenant) || Number(localStorage.getItem('tenantId')) || 1;
  }, [tenant]);

  /**
   * Initialize component
   */
  useEffect(() => {
    // Set initial content from event data
    if (eventData.title || eventData.description) {
      setPostContent(`${eventData.title || ''}\n\n${eventData.description || ''}`);
    }
    
    // Set initial media
    const initialMedia = [
      ...(eventData.images || []).map(img => ({ type: 'image', url: img })),
      ...(eventData.videos || []).map(vid => ({ type: 'video', url: vid }))
    ];
    setSelectedMedia(initialMedia);

    // Check YouTube connection
    checkYouTubeConnection(getTenantId()).then(result => {
      setYoutubeConnected(result.connected);
    });
  }, [eventData, getTenantId]);

  /**
   * Handle platform selection
   */
  const handlePlatformToggle = (platform) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platform)) {
        return prev.filter(p => p !== platform);
      }
      
      // Check if platform is YouTube and it's not connected
      if (platform === 'youtube' && !youtubeConnected) {
        setStatusMessage('⚠️ Please connect your YouTube account first in Integrations.');
        return prev;
      }
      
      return [...prev, platform];
    });
  };

  /**
   * Validate selected media for platforms
   */
  const validateMediaForPlatforms = () => {
    const errors = [];
    const hasVideo = selectedMedia.some(m => m.type === 'video');
    const hasImage = selectedMedia.some(m => m.type === 'image');

    // YouTube validation
    if (selectedPlatforms.includes('youtube')) {
      if (!hasVideo) {
        errors.push('YouTube requires at least one video');
      }
      if (hasImage) {
        errors.push('YouTube does not support image-only posts');
      }
      
      // Validate video file paths
      const videoMedia = selectedMedia.filter(m => m.type === 'video');
      for (const media of videoMedia) {
        const validation = validateYouTubeFilePath(media.url);
        if (!validation.valid) {
          errors.push(`YouTube: ${validation.message}`);
        }
      }
    }

    return errors;
  };

  /**
   * Post to YouTube
   */
  const postToYouTube = async (videoUrl) => {
    try {
      setPlatformStatus(prev => ({
        ...prev,
        youtube: { status: 'uploading', message: 'Uploading to YouTube...' }
      }));

      const { title, description } = extractYouTubeTitleDescription(postContent);
      const tags = youtubeTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const result = await uploadToYouTube({
        tenantId: getTenantId(),
        title,
        description,
        filePath: videoUrl,
        privacyStatus: youtubePrivacy,
        tags,
        isShort: youtubeIsShort,
        onProgress: (progress) => {
          setUploadProgress(prev => ({
            ...prev,
            youtube: progress.progress
          }));
        }
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      setPlatformStatus(prev => ({
        ...prev,
        youtube: { 
          status: 'success', 
          message: `✅ Posted to YouTube`,
          url: result.url 
        }
      }));

      return result;
    } catch (error) {
      setPlatformStatus(prev => ({
        ...prev,
        youtube: { 
          status: 'error', 
          message: `❌ YouTube: ${error.message}` 
        }
      }));
      throw error;
    }
  };

  /**
   * Post to other platforms
   */
  const postToOtherPlatform = async (platform) => {
    try {
      setPlatformStatus(prev => ({
        ...prev,
        [platform]: { status: 'uploading', message: `Posting to ${PLATFORMS[platform].name}...` }
      }));

      const payload = {
        postContent,
        images: selectedMedia.filter(m => m.type === 'image').map(m => m.url),
        videos: selectedMedia.filter(m => m.type === 'video').map(m => m.url),
        eventId: eventData.id,
        name: PLATFORMS[platform].name,
        userTags: Object.keys(userTags).length > 0 ? userTags : undefined,
      };

      const response = await axios.post(
        `${baseUrl}/api/integrations/postMedia/${getTenantId()}`, 
        payload
      );

      setPlatformStatus(prev => ({
        ...prev,
        [platform]: { 
          status: 'success', 
          message: `✅ Posted to ${PLATFORMS[platform].name}` 
        }
      }));

      return response.data;
    } catch (error) {
      setPlatformStatus(prev => ({
        ...prev,
        [platform]: { 
          status: 'error', 
          message: `❌ ${PLATFORMS[platform].name}: ${error.response?.data?.message || error.message}` 
        }
      }));
      throw error;
    }
  };

  /**
   * Handle post submission
   */
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedPlatforms.length) {
      setStatusMessage('⚠️ Please select at least one platform to post.');
      return;
    }

    if (!postContent.trim()) {
      setStatusMessage('⚠️ Please enter post content.');
      return;
    }

    if (selectedMedia.length === 0) {
      setStatusMessage('⚠️ Please select at least one media file.');
      return;
    }

    // Validate media for selected platforms
    const validationErrors = validateMediaForPlatforms();
    if (validationErrors.length > 0) {
      setStatusMessage(`⚠️ Validation errors:\n${validationErrors.join('\n')}`);
      return;
    }

    try {
      setSubmitting(true);
      setStatusMessage('');
      setPlatformStatus({});
      setUploadProgress({});

      const results = {
        success: [],
        failed: []
      };

      // Post to each platform
      for (const platform of selectedPlatforms) {
        try {
          if (platform === 'youtube') {
            // YouTube only supports video
            const videoMedia = selectedMedia.find(m => m.type === 'video');
            if (videoMedia) {
              await postToYouTube(videoMedia.url);
              results.success.push(platform);
            }
          } else {
            // Other platforms
            await postToOtherPlatform(platform);
            results.success.push(platform);
          }
        } catch (error) {
          console.error(`Failed to post to ${platform}:`, error);
          results.failed.push(platform);
        }
      }

      // Set final status message
      if (results.success.length > 0 && results.failed.length === 0) {
        setStatusMessage(`✅ Successfully posted to all ${results.success.length} platform(s)!`);
      } else if (results.success.length > 0) {
        setStatusMessage(
          `⚠️ Posted to ${results.success.length} platform(s), ` +
          `but failed on ${results.failed.length} platform(s). Check details below.`
        );
      } else {
        setStatusMessage('❌ Failed to post to any platform. Check details below.');
      }
    } catch (error) {
      console.error('Post submission error:', error);
      setStatusMessage('❌ An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle media file selection
   */
  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newMedia = files.map(file => ({
      type: file.type.startsWith('video/') ? 'video' : 'image',
      url: URL.createObjectURL(file),
      file: file,
      name: file.name
    }));
    setSelectedMedia(prev => [...prev, ...newMedia]);
  };

  /**
   * Remove media item
   */
  const removeMedia = (index) => {
    setSelectedMedia(prev => {
      const newMedia = [...prev];
      // Revoke object URL if it's a blob
      if (newMedia[index].url.startsWith('blob:')) {
        URL.revokeObjectURL(newMedia[index].url);
      }
      newMedia.splice(index, 1);
      return newMedia;
    });
  };

  return (
    <div className="media-post-container">
      <div className="card">
        <div className="card-header">
          <h2>Create Post</h2>
          <p>Share content across multiple platforms</p>
        </div>

        <form onSubmit={handlePostSubmit} className="card-body">
          {/* Status Message */}
          {statusMessage && (
            <div className={`alert ${statusMessage.includes('✅') ? 'alert-success' : 
                                     statusMessage.includes('⚠️') ? 'alert-warning' : 
                                     'alert-error'}`}>
              <pre>{statusMessage}</pre>
              <button 
                type="button"
                className="alert-close" 
                onClick={() => setStatusMessage('')}
              >
                ×
              </button>
            </div>
          )}

          {/* Post Content */}
          <div className="form-group">
            <label htmlFor="postContent">Post Content *</label>
            <textarea
              id="postContent"
              className="form-control"
              rows="6"
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="Write your post content here..."
              required
            />
            <small className="form-hint">
              First line will be used as the title for YouTube videos
            </small>
          </div>

          {/* Media Selection */}
          <div className="form-group">
            <label>Media Files</label>
            <div className="media-upload">
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleMediaSelect}
                className="file-input"
                id="mediaUpload"
              />
              <label htmlFor="mediaUpload" className="file-label">
                📎 Select Images or Videos
              </label>
            </div>

            {/* Media Preview */}
            {selectedMedia.length > 0 && (
              <div className="media-preview-grid">
                {selectedMedia.map((media, index) => (
                  <div key={index} className="media-preview-item">
                    {media.type === 'image' ? (
                      <MediaTagOverlay
                        src={media.url}
                        tags={userTags[index] || []}
                        onChange={(tags) => setUserTags(prev => ({ ...prev, [index]: tags }))}
                        style={{ width: '100%', height: '100%' }}
                      />
                    ) : (
                      <div className="video-preview">
                        <video src={media.url} />
                        <span className="video-badge">VIDEO</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="remove-media-btn"
                      onClick={() => removeMedia(index)}
                      title="Remove"
                    >
                      ×
                    </button>
                    <div className="media-info">
                      <small>{media.name || media.url.split('/').pop()}</small>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Platform Selection */}
          <div className="form-group">
            <label>Select Platforms *</label>
            <div className="platforms-grid">
              {Object.entries(PLATFORMS).map(([key, platform]) => (
                <div
                  key={key}
                  className={`platform-card ${selectedPlatforms.includes(key) ? 'selected' : ''} 
                             ${key === 'youtube' && !youtubeConnected ? 'disabled' : ''}`}
                  onClick={() => handlePlatformToggle(key)}
                >
                  <div className="platform-header">
                    <span className="platform-icon">{platform.icon}</span>
                    <span className="platform-name">{platform.name}</span>
                  </div>
                  <div className="platform-features">
                    {platform.supportsVideo && <span className="feature-badge">📹 Video</span>}
                    {platform.supportsImage && <span className="feature-badge">🖼️ Image</span>}
                  </div>
                  {key === 'youtube' && !youtubeConnected && (
                    <div className="platform-warning">
                      <small>Not connected</small>
                    </div>
                  )}
                  {selectedPlatforms.includes(key) && (
                    <div className="selected-indicator">✓</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* YouTube-specific options */}
          {selectedPlatforms.includes('youtube') && (
            <div className="youtube-options">
              <h4>YouTube Options</h4>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={youtubeIsShort}
                    onChange={(e) => setYoutubeIsShort(e.target.checked)}
                  />
                  <span>Upload as YouTube Short</span>
                </label>
                <small className="form-hint">
                  Shorts are vertical videos up to 60 seconds. #Shorts will be added to the title automatically.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="youtubePrivacy">Privacy Status</label>
                <select
                  id="youtubePrivacy"
                  value={youtubePrivacy}
                  onChange={(e) => setYoutubePrivacy(e.target.value)}
                  className="form-control"
                >
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
                <small className="form-hint">
                  Public: Anyone can find and view • Unlisted: Anyone with link can view • Private: Only you can view
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="youtubeTags">Tags (optional)</label>
                <input
                  type="text"
                  id="youtubeTags"
                  value={youtubeTags}
                  onChange={(e) => setYoutubeTags(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  className="form-control"
                />
                <small className="form-hint">
                  Separate tags with commas
                </small>
              </div>
            </div>
          )}

          {/* Platform Status */}
          {Object.keys(platformStatus).length > 0 && (
            <div className="platform-status-section">
              <h4>Platform Status</h4>
              <div className="platform-status-list">
                {Object.entries(platformStatus).map(([platform, status]) => (
                  <div 
                    key={platform} 
                    className={`status-item status-${status.status}`}
                  >
                    <span className="status-platform">
                      {PLATFORMS[platform]?.icon} {PLATFORMS[platform]?.name}
                    </span>
                    <span className="status-message">{status.message}</span>
                    {status.url && (
                      <a 
                        href={status.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="status-link"
                      >
                        View →
                      </a>
                    )}
                    {uploadProgress[platform] !== undefined && (
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${uploadProgress[platform]}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary btn-large"
              disabled={submitting || selectedPlatforms.length === 0}
            >
              {submitting ? (
                <>
                  <span className="spinner-small"></span>
                  Posting...
                </>
              ) : (
                <>
                  📤 Post to {selectedPlatforms.length} Platform{selectedPlatforms.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .media-post-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }

        .card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .card-header {
          padding: 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .card-header h2 {
          margin: 0 0 4px 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .card-header p {
          margin: 0;
          color: #6b7280;
          font-size: 0.875rem;
        }

        .card-body {
          padding: 24px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #374151;
        }

        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9375rem;
          transition: border-color 0.2s;
        }

        .form-control:focus {
          outline: none;
          border-color: #FF0000;
          box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
        }

        .form-hint {
          display: block;
          margin-top: 6px;
          color: #6b7280;
          font-size: 0.8125rem;
        }

        textarea.form-control {
          resize: vertical;
          min-height: 120px;
        }

        /* Media Upload */
        .media-upload {
          margin-bottom: 16px;
        }

        .file-input {
          display: none;
        }

        .file-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #f3f4f6;
          border: 2px dashed #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .file-label:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        /* Media Preview */
        .media-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          gap: 12px;
        }

        .media-preview-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #e5e7eb;
        }

        .media-preview-item img,
        .media-preview-item video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video-preview {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .video-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .remove-media-btn {
          position: absolute;
          top: 4px;
          left: 4px;
          width: 28px;
          height: 28px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          border-radius: 50%;
          font-size: 1.25rem;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .remove-media-btn:hover {
          background: #dc2626;
        }

        .media-info {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 8px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
        }

        .media-info small {
          font-size: 0.75rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }

        /* Platforms Grid */
        .platforms-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 12px;
        }

        .platform-card {
          position: relative;
          padding: 16px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .platform-card:hover:not(.disabled) {
          border-color: #FF0000;
          box-shadow: 0 2px 8px rgba(255, 0, 0, 0.1);
        }

        .platform-card.selected {
          border-color: #FF0000;
          background: #fff5f5;
        }

        .platform-card.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .platform-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .platform-icon {
          font-size: 1.5rem;
        }

        .platform-name {
          font-weight: 500;
          font-size: 0.875rem;
        }

        .platform-features {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .feature-badge {
          font-size: 0.75rem;
          padding: 2px 6px;
          background: #f3f4f6;
          border-radius: 4px;
        }

        .platform-warning {
          margin-top: 8px;
          color: #dc2626;
          font-size: 0.75rem;
        }

        .selected-indicator {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 24px;
          height: 24px;
          background: #FF0000;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        /* YouTube Options */
        .youtube-options {
          padding: 20px;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .youtube-options h4 {
          margin: 0 0 16px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #78350f;
        }

        .checkbox-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-weight: 500;
          color: #374151;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #FF0000;
          cursor: pointer;
        }

        /* Platform Status */
        .platform-status-section {
          padding: 20px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .platform-status-section h4 {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .platform-status-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .status-item.status-uploading {
          background: #dbeafe;
          border: 1px solid #93c5fd;
        }

        .status-item.status-success {
          background: #d1fae5;
          border: 1px solid #6ee7b7;
        }

        .status-item.status-error {
          background: #fee2e2;
          border: 1px solid #fca5a5;
        }

        .status-platform {
          font-weight: 500;
          min-width: 120px;
        }

        .status-message {
          flex: 1;
        }

        .status-link {
          color: #FF0000;
          text-decoration: none;
          font-weight: 500;
        }

        .status-link:hover {
          text-decoration: underline;
        }

        .progress-bar {
          width: 100%;
          height: 6px;
          background: #e5e7eb;
          border-radius: 3px;
          overflow: hidden;
          margin-top: 8px;
        }

        .progress-fill {
          height: 100%;
          background: #FF0000;
          transition: width 0.3s ease;
        }

        /* Alert */
        .alert {
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          position: relative;
        }

        .alert pre {
          margin: 0;
          white-space: pre-wrap;
          font-family: inherit;
        }

        .alert-success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #6ee7b7;
        }

        .alert-warning {
          background: #fef3c7;
          color: #78350f;
          border: 1px solid #fde68a;
        }

        .alert-error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        .alert-close {
          position: absolute;
          top: 8px;
          right: 8px;
          background: none;
          border: none;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
        }

        .alert-close:hover {
          opacity: 1;
        }

        /* Buttons */
        .form-actions {
          display: flex;
          justify-content: flex-end;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #FF0000;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #CC0000;
        }

        .btn-large {
          font-size: 1rem;
          padding: 12px 24px;
        }

        .spinner-small {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 640px) {
          .platforms-grid {
            grid-template-columns: 1fr;
          }

          .media-preview-grid {
            grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
          }
        }
      `}</style>
    </div>
  );
}

export default MediaPostEnhanced;
